import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { useSim } from "../../lib/store";
import { House, Church } from "./Buildings";

const ROADS = [-42, -28, -14, 0, 14, 28, 42];
const BLOCKS = [-35, -21, -7, 7, 21, 35];
const CAR_COLORS = ["#c0392b", "#2980b9", "#f1c40f", "#ecf0f1", "#27ae60", "#e67e22", "#8e44ad", "#16a085"];
// Mobile LOD scaling for the metropolis entity counts.
const MOBILE = typeof window !== "undefined" && window.innerWidth < 1024;

// Lava / ash impact scorch palette for the instanced skyline.
const CHAR_COL = new THREE.Color(0.3, 0.26, 0.24);
const WHITE_COL = new THREE.Color(1, 1, 1);
const WARM_EM = new THREE.Color("#ffd9a0");
const LAVA_EM = new THREE.Color("#ff5518");
const LOW_EM = new THREE.Color("#140f06");
const _tmpC1 = new THREE.Color();
const _tmpC2 = new THREE.Color();
const _tmpC3 = new THREE.Color();

function rng(seed) {
  let s = seed;
  return () => ((s = (s * 9301 + 49297) % 233280), s / 233280);
}

function streetEnv(st) {
  return st.simulating ? Math.sin(Math.min(st.simProgress, 1) * Math.PI) * 0.7 + 0.3 : 0;
}

function genBuildings() {
  const r = rng(7321);
  const towers = [];
  const lows = [];
  let total = 0;
  for (const bx of BLOCKS) {
    for (const bz of BLOCKS) {
      const per = 4 + Math.floor(r() * 3);
      for (let k = 0; k < per && total < 200; k++) {
        const x = bx + (r() - 0.5) * 9;
        const z = bz + (r() - 0.5) * 9;
        const distC = Math.hypot(x, z);
        const downtown = Math.max(0, 1 - distC / 55);
        const isTower = r() < 0.35 + downtown * 0.4;
        const w = 2 + r() * 2.5;
        const d = 2 + r() * 2.5;
        const h = isTower ? 10 + downtown * 34 + r() * 14 : 3 + r() * 5;
        const phase = r() * Math.PI * 2;
        (isTower ? towers : lows).push({ x, z, w, d, h, phase });
        total++;
      }
    }
  }
  return { towers, lows };
}

// ---- Instanced buildings: towers (deep low-freq sway) + lowrise (sharp high-freq) ----
function InstancedBuildings({ towers, lows }) {
  const towerRef = useRef();
  const lowRef = useRef();
  const towerMatRef = useRef();
  const lowMatRef = useRef();

  // procedural lit-window sprite used as the towers' emissive map (sparkles at night)
  const winTex = useMemo(() => {
    const c = document.createElement("canvas");
    c.width = 64; c.height = 128;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#000"; ctx.fillRect(0, 0, 64, 128);
    const cols = 5, rows = 12;
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (Math.random() < 0.3) {
          const warm = Math.random() < 0.7;
          ctx.fillStyle = warm ? "#ffcf8a" : "#a9d0f5";
          ctx.fillRect(x * 12 + 4, y * 10 + 3, 5, 4);
        }
      }
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(2, 6);
    return t;
  }, []);

  useEffect(() => {
    const write = (mesh, arr, cols) => {
      if (!mesh) return;
      const dummy = new THREE.Object3D();
      arr.forEach((b, i) => {
        dummy.position.set(b.x, 0, b.z);
        dummy.scale.set(b.w, b.h, b.d);
        dummy.rotation.set(0, 0, 0);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        mesh.setColorAt(i, cols[i]);
      });
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    };
    const tcols = towers.map((_, i) => new THREE.Color().setHSL(0.58, 0.28, 0.3 + (i % 4) * 0.04));
    const lcols = lows.map((_, i) => new THREE.Color(["#5a6c8f", "#6a5872", "#5f7580", "#726552"][i % 4]));
    write(towerRef.current, towers, tcols);
    write(lowRef.current, lows, lcols);
  }, [towers, lows]);

  // resonance: whole-group shear differs by stiffness (towers low freq, lows high freq)
  const useShake = (ref, freq, gain, jitter) =>
    useFrame((state) => {
      if (!ref.current) return;
      const t = state.clock.elapsedTime;
      const amp = window.__streetAmp ?? 0;
      const st = useSim.getState();
      const env = streetEnv(st);
      const liq = (window.__streetLiq ?? 0) * env;
      ref.current.rotation.z = Math.sin(t * freq) * amp * gain + liq * 0.05;
      ref.current.rotation.x = Math.cos(t * freq * 0.9) * amp * gain * 0.6;
      ref.current.position.x = jitter ? Math.sin(t * freq * 1.3) * amp * jitter : 0;
      ref.current.position.y = -liq * 0.9;
    });
  useShake(towerRef, 2.0, 3.4, 0);
  useShake(lowRef, 20, 0.9, 2.2);

  // window lights ramp up at night + lava/ash scorch (char + orange heat glow + slump)
  useFrame(() => {
    const night = window.__streetNight ?? 0;
    const st = useSim.getState();
    const volcanic = st.events.some((e) => e.type === "volcanic" || e.activeEruption);
    const scorch = volcanic && st.simulating ? Math.min(st.simProgress * 1.3, 1) : 0;
    window.__streetScorch = scorch; // shared with House/Church landmarks
    const tcol = _tmpC1.copy(WHITE_COL).lerp(CHAR_COL, scorch);
    if (towerMatRef.current) {
      const m = towerMatRef.current;
      m.color.lerp(tcol, 0.1);
      m.emissive.lerp(_tmpC2.copy(WARM_EM).lerp(LAVA_EM, scorch), 0.15);
      m.emissiveIntensity = night * night * 0.9 + scorch * 1.5;
    }
    if (lowMatRef.current) {
      const m = lowMatRef.current;
      m.color.lerp(tcol, 0.1);
      m.emissive.lerp(_tmpC3.copy(LOW_EM).lerp(LAVA_EM, scorch), 0.15);
      m.emissiveIntensity = 0.4 + scorch * 1.4;
    }
    const slump = 1 - scorch * 0.06;
    if (towerRef.current) towerRef.current.scale.y += (slump - towerRef.current.scale.y) * 0.08;
    if (lowRef.current) lowRef.current.scale.y += (slump - lowRef.current.scale.y) * 0.08;
  });

  // geometry is a unit box with base at y=0 (translated), so instance scale.y = height
  const boxGeo = useMemo(() => {
    const g = new THREE.BoxGeometry(1, 1, 1);
    g.translate(0, 0.5, 0);
    return g;
  }, []);

  return (
    <group>
      <instancedMesh ref={towerRef} args={[boxGeo, undefined, towers.length]} castShadow receiveShadow>
        <meshStandardMaterial ref={towerMatRef} vertexColors metalness={0.18} roughness={0.55} emissive="#ffd9a0" emissiveMap={winTex} emissiveIntensity={0.7} />
      </instancedMesh>
      <instancedMesh ref={lowRef} args={[boxGeo, undefined, lows.length]} receiveShadow>
        <meshStandardMaterial ref={lowMatRef} vertexColors metalness={0.2} roughness={0.8} emissive="#140f06" emissiveIntensity={0.4} />
      </instancedMesh>
    </group>
  );
}

// ---- 150 instanced cars driving the road grid ----
function InstancedCars({ count = 150 }) {
  const bodyRef = useRef();
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const cars = useMemo(() => {
    const r = rng(4113);
    const arr = [];
    for (let i = 0; i < count; i++) {
      const axis = r() < 0.5 ? "x" : "z";
      const road = ROADS[Math.floor(r() * ROADS.length)];
      const lane = r() < 0.5 ? 2.2 : -2.2;
      const dir = lane > 0 ? 1 : -1;
      arr.push({
        axis, fixed: road + lane, dir, speed: 4 + r() * 8, p: r() * 100 - 50,
        color: new THREE.Color(CAR_COLORS[i % CAR_COLORS.length]),
        floating: false, fx: 0, fy: 0.18, fz: 0, vx: 0, vz: 0, spin: (r() - 0.5) * 2,
      });
    }
    return arr;
  }, [count]);

  useEffect(() => {
    const m = bodyRef.current;
    if (!m) return;
    cars.forEach((c, i) => m.setColorAt(i, c.color));
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  }, [cars]);

  useFrame((state, delta) => {
    const m = bodyRef.current;
    if (!m) return;
    const dt = Math.min(delta, 0.05);
    const amp = window.__streetAmp ?? 0;
    const t = state.clock.elapsedTime;
    const flood = window.__flood;
    const floodOn = flood && flood.active && flood.level > 0.35;
    for (let i = 0; i < cars.length; i++) {
      const c = cars[i];
      c.p += c.dir * c.speed * dt;
      if (c.p > 55) c.p = -55;
      if (c.p < -55) c.p = 55;

      if (floodOn) {
        // Hydro-drift: lift the car to the water surface and carry it with the current.
        if (!c.floating) {
          c.floating = true;
          c.fx = c.axis === "x" ? c.p : c.fixed;
          c.fz = c.axis === "x" ? c.fixed : c.p;
          c.fy = 0.18;
          c.vx = flood.dirX * (0.4 + Math.random()) * flood.strength;
          c.vz = flood.dirZ * (0.4 + Math.random()) * flood.strength;
        }
        c.vx += Math.sin(t * 0.7 + i) * 0.5 * dt;
        c.vz += Math.cos(t * 0.6 + i) * 0.5 * dt;
        c.fx += c.vx * dt;
        c.fz += c.vz * dt;
        if (c.fx > 72) c.fx = -72;
        if (c.fx < -72) c.fx = 72;
        if (c.fz > 72) c.fz = -72;
        if (c.fz < -72) c.fz = 72;
        c.fy += (flood.level + 0.16 - c.fy) * 0.06;
        dummy.position.set(c.fx, c.fy + Math.sin(t * 2 + i) * 0.08, c.fz);
        dummy.rotation.set(Math.sin(t * 1.5 + i) * 0.25, c.spin * t * 0.4, Math.cos(t * 1.3 + i) * 0.3);
      } else {
        c.floating = false;
        const bounce = Math.abs(Math.sin(t * 30 + i)) * amp * 3;
        if (c.axis === "x") {
          dummy.position.set(c.p, 0.18 + bounce, c.fixed);
          dummy.rotation.set(0, c.dir > 0 ? Math.PI / 2 : -Math.PI / 2, Math.sin(t * 26 + i) * amp * 3);
        } else {
          dummy.position.set(c.fixed, 0.18 + bounce, c.p);
          dummy.rotation.set(0, c.dir > 0 ? 0 : Math.PI, Math.sin(t * 26 + i) * amp * 3);
        }
      }
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      m.setMatrixAt(i, dummy.matrix);
    }
    m.instanceMatrix.needsUpdate = true;
  });

  const carGeo = useMemo(() => new THREE.BoxGeometry(0.9, 0.4, 1.9), []);
  return (
    <instancedMesh ref={bodyRef} args={[carGeo, undefined, count]}>
      <meshStandardMaterial vertexColors metalness={0.5} roughness={0.4} />
    </instancedMesh>
  );
}

// ---- Instanced trees that sway with seismic physics ----
function InstancedTrees({ count = 160 }) {
  const trunkRef = useRef();
  const leafRef = useRef();
  const trees = useMemo(() => {
    const r = rng(9987);
    const arr = [];
    for (let i = 0; i < count; i++) {
      const road = ROADS[Math.floor(r() * ROADS.length)];
      const along = (r() - 0.5) * 100;
      const side = r() < 0.5 ? 3.6 : -3.6;
      const onX = r() < 0.5;
      arr.push({ x: onX ? along : road + side, z: onX ? road + side : along, s: 0.8 + r() * 0.6, phase: r() * 6.28 });
    }
    return arr;
  }, [count]);

  useEffect(() => {
    const dummy = new THREE.Object3D();
    trees.forEach((tr, i) => {
      dummy.position.set(tr.x, 0, tr.z);
      dummy.scale.set(tr.s, tr.s, tr.s);
      dummy.updateMatrix();
      trunkRef.current?.setMatrixAt(i, dummy.matrix);
      dummy.position.set(tr.x, tr.s * 1.4, tr.z);
      dummy.scale.setScalar(tr.s);
      dummy.updateMatrix();
      leafRef.current?.setMatrixAt(i, dummy.matrix);
    });
    if (trunkRef.current) trunkRef.current.instanceMatrix.needsUpdate = true;
    if (leafRef.current) leafRef.current.instanceMatrix.needsUpdate = true;
  }, [trees]);

  const groupRef = useRef();
  useFrame((state) => {
    if (!groupRef.current) return;
    const amp = window.__streetAmp ?? 0;
    const t = state.clock.elapsedTime;
    groupRef.current.rotation.z = Math.sin(t * 8) * amp * 4 + Math.sin(t * 1.5) * 0.01;
  });

  const trunkGeo = useMemo(() => { const g = new THREE.CylinderGeometry(0.1, 0.15, 1.4, 6); g.translate(0, 0.7, 0); return g; }, []);
  const leafGeo = useMemo(() => new THREE.IcosahedronGeometry(0.7, 0), []);
  return (
    <group ref={groupRef}>
      <instancedMesh ref={trunkRef} args={[trunkGeo, undefined, count]}>
        <meshStandardMaterial color="#5a3d28" roughness={0.9} />
      </instancedMesh>
      <instancedMesh ref={leafRef} args={[leafGeo, undefined, count]}>
        <meshStandardMaterial color="#2f6b3a" roughness={0.85} flatShading />
      </instancedMesh>
    </group>
  );
}

// ---- Utility poles + electrical sparks on strong shaking ----
function UtilityPoles({ count = 56 }) {
  const poleRef = useRef();
  const wireRef = useRef();
  const sparkRef = useRef();
  const poles = useMemo(() => {
    const r = rng(555);
    const arr = [];
    for (let i = 0; i < count; i++) {
      const road = ROADS[Math.floor(r() * ROADS.length)];
      const along = (r() - 0.5) * 100;
      const onX = r() < 0.5;
      arr.push({ x: onX ? along : road + 4.4, z: onX ? road + 4.4 : along });
    }
    return arr;
  }, [count]);

  const sparkGeo = useMemo(() => {
    const N = count;
    const pos = new Float32Array(N * 3);
    poles.forEach((p, i) => { pos[i * 3] = p.x; pos[i * 3 + 1] = 2.7; pos[i * 3 + 2] = p.z; });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, [poles, count]);

  useEffect(() => {
    const dummy = new THREE.Object3D();
    poles.forEach((p, i) => {
      dummy.position.set(p.x, 0, p.z);
      dummy.updateMatrix();
      poleRef.current?.setMatrixAt(i, dummy.matrix);
      dummy.position.set(p.x, 2.7, p.z);
      dummy.updateMatrix();
      wireRef.current?.setMatrixAt(i, dummy.matrix);
    });
    if (poleRef.current) poleRef.current.instanceMatrix.needsUpdate = true;
    if (wireRef.current) wireRef.current.instanceMatrix.needsUpdate = true;
  }, [poles]);

  useFrame((state) => {
    const st = useSim.getState();
    const s = sparkRef.current;
    if (!s) return;
    const on = st.simulating && (window.__streetAmp ?? 0) > 0.004;
    s.visible = on;
    if (on) {
      s.material.opacity = 0.4 + Math.abs(Math.sin(state.clock.elapsedTime * 40)) * 0.6;
      s.material.size = 0.25 + Math.random() * 0.3;
    }
  });

  const poleGeo = useMemo(() => { const g = new THREE.CylinderGeometry(0.08, 0.1, 3, 6); g.translate(0, 1.5, 0); return g; }, []);
  const crossGeo = useMemo(() => new THREE.BoxGeometry(1.4, 0.1, 0.1), []);
  return (
    <group>
      <instancedMesh ref={poleRef} args={[poleGeo, undefined, count]} castShadow>
        <meshStandardMaterial color="#3a2f26" roughness={0.9} />
      </instancedMesh>
      <instancedMesh ref={wireRef} args={[crossGeo, undefined, count]}>
        <meshStandardMaterial color="#2a2420" roughness={0.9} />
      </instancedMesh>
      <points ref={sparkRef} geometry={sparkGeo} visible={false}>
        <pointsMaterial color="#bfe6ff" size={0.3} sizeAttenuation transparent opacity={0.8} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
      </points>
    </group>
  );
}

// ---- Elevated highway that collapses in high-magnitude events ----
function Highway() {
  const segRefs = useRef([]);
  const SEGS = 11;
  const seg = useMemo(
    () => Array.from({ length: SEGS }, (_, i) => ({ x: -50 + i * 10, y0: 6, y: 6, vel: 0, rot: 0, threshold: 0.25 + (i % 4) * 0.14 })),
    []
  );
  useFrame((_, delta) => {
    const st = useSim.getState();
    const dt = Math.min(delta, 0.05);
    const collapsing = st.simulating && st.simProgress > 0.1 && st.events.some((e) => e.magnitude >= 7.5);
    seg.forEach((s, i) => {
      const m = segRefs.current[i];
      if (!m) return;
      if (collapsing && st.simProgress > s.threshold) {
        s.vel -= 9 * dt;
        s.y += s.vel * dt;
        s.rot += dt * 0.8;
        if (s.y < 0.4) { s.y = 0.4; s.vel = 0; }
      } else {
        s.y += (s.y0 - s.y) * 0.08;
        s.rot += (0 - s.rot) * 0.08;
        s.vel = 0;
      }
      m.position.y = s.y;
      m.rotation.z = s.rot;
    });
  });
  return (
    <group position={[0, 0, 22]}>
      {seg.map((s, i) => (
        <group key={i} ref={(el) => (segRefs.current[i] = el)} position={[s.x, s.y0, 0]}>
          <mesh castShadow><boxGeometry args={[9.6, 0.5, 5]} /><meshStandardMaterial color="#4a4f57" roughness={0.85} metalness={0.2} /></mesh>
        </group>
      ))}
      {seg.filter((_, i) => i % 2 === 0).map((s, i) => (
        <mesh key={`p${i}`} position={[s.x, 3, 0]} castShadow><boxGeometry args={[1, 6, 1]} /><meshStandardMaterial color="#3a3f47" roughness={0.9} /></mesh>
      ))}
    </group>
  );
}

// ---- Volcanic ash fall + lava flow for cities near an eruption ----
function VolcanicStreet() {
  const ashRef = useRef();
  const lavaRef = useRef();
  const events = useSim((s) => s.events);
  const volcanic = events.some((e) => e.type === "volcanic" || e.activeEruption);

  const ashGeo = useMemo(() => {
    const N = 1200;
    const pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 110;
      pos[i * 3 + 1] = Math.random() * 40;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 110;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, []);

  useFrame((state, delta) => {
    const st = useSim.getState();
    const dt = Math.min(delta, 0.05);
    if (ashRef.current) {
      ashRef.current.visible = volcanic;
      if (volcanic) {
        const arr = ashGeo.attributes.position.array;
        for (let i = 0; i < arr.length; i += 3) {
          arr[i + 1] -= (2 + (i % 5)) * dt;
          arr[i] += Math.sin(state.clock.elapsedTime + i) * 0.01;
          if (arr[i + 1] < 0.1) arr[i + 1] = 40;
        }
        ashGeo.attributes.position.needsUpdate = true;
      }
    }
    if (lavaRef.current) {
      const active = volcanic && st.simulating;
      lavaRef.current.visible = active;
      if (active) {
        const grow = 6 + st.simProgress * 90;
        lavaRef.current.scale.set(grow, 1, grow * 0.4);
        lavaRef.current.material.emissiveIntensity = 1.5 + Math.sin(state.clock.elapsedTime * 4) * 0.6;
      }
    }
  });

  if (!volcanic) return null;
  return (
    <group>
      <points ref={ashRef} geometry={ashGeo} frustumCulled={false}>
        <pointsMaterial color="#9a9a9a" size={0.32} sizeAttenuation transparent opacity={0.5} depthWrite={false} />
      </points>
      <mesh ref={lavaRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.06, -30]} visible={false}>
        <planeGeometry args={[1, 1]} />
        <meshStandardMaterial color="#ff5a1a" emissive="#ff3300" emissiveIntensity={2} transparent opacity={0.9} toneMapped={false} />
      </mesh>
    </group>
  );
}

export function Metropolis() {
  const buildings = useMemo(() => genBuildings(), []);
  const basePositions = useMemo(
    () => [...buildings.towers, ...buildings.lows].map((b) => ({ x: b.x, z: b.z })),
    [buildings]
  );
  return (
    <group>
      <InstancedBuildings towers={buildings.towers} lows={buildings.lows} />
      <RooftopBeacons towers={buildings.towers} />
      <Neighborhood />
      <InstancedCars count={MOBILE ? 70 : 150} />
      <InstancedTrees count={MOBILE ? 70 : 160} />
      <UtilityPoles count={MOBILE ? 28 : 56} />
      <TrafficLights />
      <Highway />
      <VolcanicStreet />
      <FloodFoam positions={basePositions} />
    </group>
  );
}

// ---- Residential neighborhood: detailed pitched-roof houses + a landmark church ----
function Neighborhood() {
  const homes = useMemo(() => {
    const r = rng(2024);
    const arr = [];
    // residential clusters tucked into the outer blocks, away from downtown towers
    const resBlocks = [
      [-35, -35], [-35, -21], [-21, -35], [-35, 21],
      [35, 35], [21, 35], [35, 21], [35, -35], [21, -35],
    ];
    resBlocks.forEach((b) => {
      const per = 2 + Math.floor(r() * 2);
      for (let k = 0; k < per; k++) {
        arr.push({
          x: b[0] + (r() - 0.5) * 8,
          z: b[1] + (r() - 0.5) * 8,
          seed: Math.floor(r() * 1000),
          s: 1.6 + r() * 0.6,
        });
      }
    });
    return arr;
  }, []);

  return (
    <group>
      {homes.map((h, i) => (
        <group key={i} position={[h.x, 0, h.z]} scale={h.s}>
          <House x={0} z={0} seed={h.seed} />
        </group>
      ))}
      {/* landmark church with bell tower + steeple */}
      <group position={[-35, 0, 0]} scale={1.5}>
        <Church x={0} z={0} seed={7} />
      </group>
    </group>
  );
}

// ---- Foam splash line that churns at the water contact point on every building ----
function FloodFoam({ positions }) {
  const ref = useRef();
  const geo = useMemo(() => {
    const N = positions.length;
    const pos = new Float32Array(N * 3);
    positions.forEach((p, i) => {
      pos[i * 3] = p.x;
      pos[i * 3 + 1] = 0;
      pos[i * 3 + 2] = p.z;
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    return g;
  }, [positions]);

  useFrame((state) => {
    const p = ref.current;
    if (!p) return;
    const flood = window.__flood;
    const on = flood && flood.active && flood.level > 0.25;
    p.visible = on;
    if (on) {
      p.position.y = flood.level + 0.08;
      const churn = Math.abs(Math.sin(state.clock.elapsedTime * 6));
      p.material.opacity = 0.5 + churn * 0.45;
      p.material.size = 1.6 + churn * 0.6;
    }
  });

  return (
    <points ref={ref} geometry={geo} visible={false} frustumCulled={false}>
      <pointsMaterial color="#eafcff" size={1.8} sizeAttenuation transparent opacity={0.8} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
    </points>
  );
}

// ---- Traffic signals at intersections: cycle green→amber→red, flicker & fail while shaking ----
function TrafficLights() {
  const ref = useRef();
  const pts = useMemo(() => {
    const a = [];
    for (const x of ROADS) for (const z of ROADS) a.push([x + 2.4, z + 2.4]);
    return a;
  }, []);
  const cols = useMemo(() => [new THREE.Color("#16c60c"), new THREE.Color("#ffb300"), new THREE.Color("#ff2a2a")], []);
  const tmp = useMemo(() => new THREE.Color(), []);

  useEffect(() => {
    const m = ref.current;
    if (!m) return;
    const d = new THREE.Object3D();
    pts.forEach((p, i) => {
      d.position.set(p[0], 3.2, p[1]);
      d.updateMatrix();
      m.setMatrixAt(i, d.matrix);
    });
    m.instanceMatrix.needsUpdate = true;
  }, [pts]);

  useFrame((state) => {
    const m = ref.current;
    if (!m) return;
    const st = useSim.getState();
    const amp = window.__streetAmp ?? 0;
    const t = state.clock.elapsedTime;
    const failing = st.simulating && amp > 0.005;
    for (let i = 0; i < pts.length; i++) {
      const phase = (t * 0.22 + i * 0.137) % 1;
      const ci = phase < 0.55 ? 0 : phase < 0.7 ? 1 : 2;
      tmp.copy(cols[ci]);
      if (failing) {
        // power fault: erratic flicker, sometimes fully dark
        const f = Math.sin(t * 45 + i * 7.3);
        tmp.multiplyScalar(f > 0.1 ? 0.3 + Math.random() * 0.7 : 0.03);
      }
      m.setColorAt(i, tmp);
    }
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
  });

  return (
    <instancedMesh ref={ref} args={[undefined, undefined, pts.length]} frustumCulled={false}>
      <sphereGeometry args={[0.17, 8, 8]} />
      <meshBasicMaterial vertexColors toneMapped={false} />
    </instancedMesh>
  );
}

// ---- Blinking red aircraft-warning beacons: tall towers (fast) + mid towers (slow) ----
function RooftopBeacons({ towers }) {
  const refs = useRef([]);
  const tall = useMemo(() => [...towers].sort((a, b) => b.h - a.h).slice(0, 42), [towers]);

  useFrame((state) => {
    const night = window.__streetNight ?? 0;
    const t = state.clock.elapsedTime;
    tall.forEach((b, i) => {
      const m = refs.current[i];
      if (!m) return;
      const mid = i >= 16; // mid-height towers blink slowly
      const freq = mid ? 1.1 : 3.0;
      const on = Math.sin(t * freq + i * 1.3) > (mid ? 0.4 : 0.25);
      const blink = on ? 1 : mid ? 0.08 : 0.12;
      m.material.emissiveIntensity = ((mid ? 0.4 : 0.6) + night * (mid ? 1.8 : 2.6)) * blink;
      m.material.opacity = 0.4 + 0.6 * blink;
      m.scale.setScalar((mid ? 0.6 : 0.85) + blink * 0.3);
    });
  });

  return (
    <group>
      {tall.map((b, i) => (
        <mesh key={i} ref={(el) => (refs.current[i] = el)} position={[b.x, b.h + 0.25, b.z]}>
          <sphereGeometry args={[i >= 16 ? 0.18 : 0.24, 8, 8]} />
          <meshStandardMaterial color="#ff2a2a" emissive="#ff0000" emissiveIntensity={2} transparent opacity={1} depthWrite={false} toneMapped={false} />
        </mesh>
      ))}
    </group>
  );
}
